import React, { createContext, useContext, useReducer, useEffect, useCallback, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon, CloseIcon, ErrorIcon, WarningIcon } from '../components/feather-icons';
import webSocketService, {
    ProgressUpdateData,
    OcrJobStatusData,
    OcrJobProgressData
} from '../services/websocket.service';
import { authService } from '../services/auth.service';
import '../components/rag-chat-shell.css';

// ===========================
// TYPES
// ===========================

/**
 * Upload job tracking data
 */
export interface UploadJob {
    jobId: string;
    projectId: string;
    filename: string;
    status: 'uploading' | 'processing' | 'completed' | 'failed' | 'cancelled';
    progress: number;
    currentStep?: string;
    startTime: number;
    endTime?: number;
    error?: string;
    lastUpdateTime: number;
}

/**
 * Toast notification data
 */
interface ToastNotification {
    id: string;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
    autoHideDuration?: number;
}

/**
 * Upload progress state
 */
interface UploadProgressState {
    activeUploads: Map<string, UploadJob>;
    recentCompleted: UploadJob[];
    hasUnseenCompletions: boolean;
}

/**
 * Reducer action types
 */
type UploadAction =
    | { type: 'ADD_UPLOAD'; payload: UploadJob }
    | { type: 'UPDATE_PROGRESS'; payload: { jobId: string; progress: number; status?: UploadJob['status']; currentStep?: string } }
    | { type: 'COMPLETE_UPLOAD'; payload: { jobId: string; success: boolean; error?: string } }
    | { type: 'MARK_SEEN' }
    | { type: 'REMOVE_UPLOAD'; payload: string };

// ===========================
// CONTEXT
// ===========================

interface UploadProgressContextType {
    state: UploadProgressState;
    addUpload: (job: Omit<UploadJob, 'startTime' | 'lastUpdateTime'>) => void;
    markCompletionsSeen: () => void;
    getActiveUploadCount: () => number;
}

const UploadProgressContext = createContext<UploadProgressContextType | null>(null);

// ===========================
// REDUCER
// ===========================

function uploadReducer(state: UploadProgressState, action: UploadAction): UploadProgressState {
    switch (action.type) {
        case 'ADD_UPLOAD': {
            const newMap = new Map(state.activeUploads);
            newMap.set(action.payload.jobId, action.payload);
            return { ...state, activeUploads: newMap };
        }
        case 'UPDATE_PROGRESS': {
            const newMap = new Map(state.activeUploads);
            const existing = newMap.get(action.payload.jobId);
            if (existing) {
                newMap.set(action.payload.jobId, {
                    ...existing,
                    progress: action.payload.progress,
                    status: action.payload.status || existing.status,
                    currentStep: action.payload.currentStep || existing.currentStep,
                    lastUpdateTime: Date.now()
                });
            }
            return { ...state, activeUploads: newMap };
        }
        case 'COMPLETE_UPLOAD': {
            const newMap = new Map(state.activeUploads);
            const job = newMap.get(action.payload.jobId);
            if (job) {
                const completedJob: UploadJob = {
                    ...job,
                    status: action.payload.success ? 'completed' : 'failed',
                    progress: action.payload.success ? 100 : job.progress,
                    endTime: Date.now(),
                    error: action.payload.error
                };
                newMap.delete(action.payload.jobId);
                return {
                    ...state,
                    activeUploads: newMap,
                    recentCompleted: [completedJob, ...state.recentCompleted].slice(0, 10),
                    hasUnseenCompletions: true
                };
            }
            return state;
        }
        case 'MARK_SEEN':
            return { ...state, hasUnseenCompletions: false };
        case 'REMOVE_UPLOAD': {
            const newMap = new Map(state.activeUploads);
            newMap.delete(action.payload);
            return { ...state, activeUploads: newMap };
        }
        default:
            return state;
    }
}

// ===========================
// PROVIDER COMPONENT
// ===========================

export const UploadProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { t } = useTranslation();

    // Ref to hold latest t function - prevents WebSocket reconnection on language change
    const tRef = useRef(t);
    useEffect(() => {
        tRef.current = t;
    }, [t]);

    // Ref to track subscribed job IDs for cleanup
    const subscribedJobsRef = useRef<Set<string>>(new Set());

    const [state, dispatch] = useReducer(uploadReducer, {
        activeUploads: new Map(),
        recentCompleted: [],
        hasUnseenCompletions: false
    });

    // Toast notification state
    const [toasts, setToasts] = useState<ToastNotification[]>([]);

    // Stall detection constants
    const STALL_CHECK_INTERVAL = 30000; // 30 seconds
    const STALL_TIMEOUT = 5 * 60 * 1000; // 5 minutes

    // Show toast notification
    const showToast = useCallback((message: string, severity: ToastNotification['severity']) => {
        const id = `toast-${Date.now()}`;
        const autoHideDuration = 5000;
        setToasts(prev => [...prev, { id, message, severity, autoHideDuration }]);
        window.setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, autoHideDuration);
    }, []);

    // Close toast notification
    const closeToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    // Cleanup function to unsubscribe from completed job updates
    const cleanupJobSubscription = useCallback((jobId: string) => {
        if (subscribedJobsRef.current.has(jobId)) {
            webSocketService.unsubscribeProgress(jobId);
            webSocketService.unsubscribeOcrJob(jobId);
            subscribedJobsRef.current.delete(jobId);
            console.log(`[UploadProgressContext] 🧹 Unsubscribed from job: ${jobId}`);
        }
    }, []);

    // Setup WebSocket listeners
    useEffect(() => {
        const user = authService.getUserInfo();
        if (!user) {
            console.log('[UploadProgressContext] No user logged in, skipping WebSocket setup');
            return;
        }

        // Track if component is still mounted
        let isMounted = true;
        let unsubProgress: (() => void) | null = null;
        let unsubOcrStatus: (() => void) | null = null;
        let unsubOcrProgress: (() => void) | null = null;

        // Connect WebSocket first, then register listeners
        const setupWebSocket = async () => {
            console.log('[UploadProgressContext] 🔌 Connecting WebSocket...');
            const connected = await webSocketService.connect();

            if (!isMounted) return;

            if (connected) {
                console.log('[UploadProgressContext] ✅ WebSocket connected, authenticating...');
                webSocketService.authenticateForProgress(user.id, user.company);

                // Register listeners AFTER connection is established
                unsubProgress = webSocketService.onProgressUpdate((data: ProgressUpdateData) => {
                    if (!isMounted) return;
                    console.log('[UploadProgressContext] Progress update received:', data);
                    dispatch({
                        type: 'UPDATE_PROGRESS',
                        payload: {
                            jobId: data.taskId,
                            progress: data.progress,
                            status: data.status as UploadJob['status'],
                            currentStep: data.currentFile || data.currentStep
                        }
                    });

                    if (data.status === 'completed') {
                        dispatch({ type: 'COMPLETE_UPLOAD', payload: { jobId: data.taskId, success: true } });
                        showToast(tRef.current('uploadProgress.completed', 'File processing completed'), 'success');
                        cleanupJobSubscription(data.taskId);
                    } else if (data.status === 'failed') {
                        dispatch({
                            type: 'COMPLETE_UPLOAD',
                            payload: { jobId: data.taskId, success: false, error: data.details?.error }
                        });
                        showToast(tRef.current('uploadProgress.failed', 'File processing failed'), 'error');
                        cleanupJobSubscription(data.taskId);
                    }
                });

                unsubOcrStatus = webSocketService.onOcrJobStatus((data: OcrJobStatusData) => {
                    if (!isMounted) return;
                    console.log('[UploadProgressContext] OCR job status received:', data);
                    // Use uploadJobId if available, fallback to jobId
                    const targetJobId = data.data.uploadJobId || data.data.jobId;
                    if (data.data.status === 'completed') {
                        dispatch({ type: 'COMPLETE_UPLOAD', payload: { jobId: targetJobId, success: true } });
                        showToast(tRef.current('uploadProgress.ocrCompleted', 'OCR processing completed'), 'success');
                        cleanupJobSubscription(targetJobId);
                    } else if (data.data.status === 'failed') {
                        dispatch({
                            type: 'COMPLETE_UPLOAD',
                            payload: { jobId: targetJobId, success: false, error: data.data.message }
                        });
                        showToast(tRef.current('uploadProgress.ocrFailed', 'OCR processing failed'), 'error');
                        cleanupJobSubscription(targetJobId);
                    }
                });

                unsubOcrProgress = webSocketService.onOcrJobProgress((data: OcrJobProgressData) => {
                    if (!isMounted) return;
                    // Use uploadJobId if available, fallback to jobId
                    const targetJobId = data.data.uploadJobId || data.data.jobId;
                    dispatch({
                        type: 'UPDATE_PROGRESS',
                        payload: {
                            jobId: targetJobId,
                            progress: data.data.progress?.percentage || 0,
                            currentStep: data.data.currentPage
                                ? tRef.current('uploadProgress.processingPage', 'Processing page {{page}}', { page: data.data.currentPage })
                                : undefined
                        }
                    });
                });

                console.log('[UploadProgressContext] ✅ Event listeners registered');
            } else {
                console.warn('[UploadProgressContext] ⚠️ WebSocket connection failed');
            }
        };

        setupWebSocket();

        return () => {
            isMounted = false;
            unsubProgress?.();
            unsubOcrStatus?.();
            unsubOcrProgress?.();
        };
    }, [showToast, cleanupJobSubscription]);

    // Stall detection - check for jobs with no progress updates
    useEffect(() => {
        const checkForStalledJobs = () => {
            const now = Date.now();
            state.activeUploads.forEach((job, jobId) => {
                const timeSinceUpdate = now - job.lastUpdateTime;
                if (timeSinceUpdate > STALL_TIMEOUT) {
                    console.warn(`[UploadProgressContext] ⚠️ Job ${jobId} stalled (no update for ${Math.round(timeSinceUpdate / 1000)}s)`);
                    dispatch({
                        type: 'COMPLETE_UPLOAD',
                        payload: {
                            jobId,
                            success: false,
                            error: tRef.current('uploadProgress.stalledError', 'Upload stalled - no progress for 5 minutes')
                        }
                    });
                    cleanupJobSubscription(jobId);
                }
            });
        };

        const interval = setInterval(checkForStalledJobs, STALL_CHECK_INTERVAL);
        return () => clearInterval(interval);
    }, [state.activeUploads, cleanupJobSubscription, STALL_CHECK_INTERVAL, STALL_TIMEOUT]);

    // Add upload and subscribe to WebSocket updates
    const addUpload = useCallback((job: Omit<UploadJob, 'startTime' | 'lastUpdateTime'>) => {
        const now = Date.now();
        const fullJob: UploadJob = { ...job, startTime: now, lastUpdateTime: now };
        dispatch({ type: 'ADD_UPLOAD', payload: fullJob });

        // Ensure connection and authentication before subscribing
        const subscribeToJob = async () => {
            try {
                if (!webSocketService.isSocketConnected()) {
                    console.log('[UploadProgressContext] WebSocket not connected, connecting...');
                    await webSocketService.connect();
                }

                if (webSocketService.isSocketConnected()) {
                    // Ensure authentication before subscribing
                    const user = authService.getUserInfo();
                    if (user) {
                        webSocketService.authenticateForProgress(user.id, user.company);
                    }

                    // Add to tracking set BEFORE subscribing to prevent race condition
                    // where cleanup might miss the job if it completes immediately
                    subscribedJobsRef.current.add(job.jobId);
                    webSocketService.subscribeProgress(job.jobId);
                    webSocketService.subscribeOcrJob(job.jobId);
                    console.log(`[UploadProgressContext] ✅ Subscribed to job: ${job.jobId}`);
                } else {
                    console.warn(`[UploadProgressContext] ⚠️ Failed to subscribe to job: ${job.jobId}`);
                }
            } catch (error) {
                console.error(`[UploadProgressContext] ❌ Error subscribing to job ${job.jobId}:`, error);
            }
        };

        subscribeToJob();
    }, []);

    // Mark completions as seen
    const markCompletionsSeen = useCallback(() => {
        dispatch({ type: 'MARK_SEEN' });
    }, []);

    // Get active upload count
    const getActiveUploadCount = useCallback(() => {
        return state.activeUploads.size;
    }, [state.activeUploads]);

    const renderToastIcon = (severity: ToastNotification['severity']) => {
        if (severity === 'success') {
            return <CheckCircleIcon fontSize="small" />;
        }
        if (severity === 'warning') {
            return <WarningIcon fontSize="small" />;
        }
        return <ErrorIcon fontSize="small" />;
    };

    return (
        <UploadProgressContext.Provider value={{ state, addUpload, markCompletionsSeen, getActiveUploadCount }}>
            {children}

            {/* Toast notifications */}
            {toasts.length > 0 && (
                <div className="industrial-toast-stack">
                    {toasts.map((toast) => (
                        <div className={`industrial-toast industrial-toast--${toast.severity}`} role="status" key={toast.id}>
                            {renderToastIcon(toast.severity)}
                            <span>{toast.message}</span>
                            <button type="button" onClick={() => closeToast(toast.id)} aria-label={tRef.current('close', 'Close')}>
                                <CloseIcon fontSize="small" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </UploadProgressContext.Provider>
    );
};

// ===========================
// HOOK
// ===========================

export const useUploadProgress = () => {
    const context = useContext(UploadProgressContext);
    if (!context) {
        throw new Error('useUploadProgress must be used within UploadProgressProvider');
    }
    return context;
};

export default UploadProgressContext;
